import { asArray } from "@/lib/asArray";
import { fmtMonth } from "@/lib/dt";
import React, { useEffect, useMemo, useState } from "react";
import { fmtDateTimeFirm } from "@/lib/dt";
import api, { API_URL, toErrMsg } from "@/lib/api";
import { useEngagement } from "@/contexts/EngagementContext";
import { useI18n } from "@/contexts/I18nContext";
import StoredResultBanner from "@/components/StoredResultBanner";   // Batch 27 · Add-on 2 Step 2
import TbOnlyNotice from "@/components/TbOnlyNotice";   // PR 3 · rule 1 (B-1a): a monthly series needs journal lines
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Loader2, Wand2, TableProperties, Download, ChevronDown,
  Plus, X, Info, RotateCcw,
} from "lucide-react";
import DrilldownModal from "./DrilldownModal";
import ReadoutCard from "./ReadoutCard";   // D12 — AI Readout
import FyPeriodNote from "./FyPeriodNote";   // STEP 7b · F6: the financial year analysed + the PY comparison
import { useResizableColumns, ResizableTh, ResetCaption } from "@/components/ui/resizable-table";
import { fmtNumber } from "@/components/Amount";   // STEP 3 · rule 2 · B-2e: the one amount formatter
import { jobErrorText } from "@/lib/jobErrorText";   // Step 11d · A2: a failed job's error in the screen's language

const POLL_INTERVAL_MS = 2000;

const pairKey = (a, b) => (a <= b ? `${a}__${b}` : `${b}__${a}`);

const fmtVariancePct = (v) => {
  const pct = v * 100;
  const abs = Math.abs(pct);
  const arrow = pct > 0 ? "↑" : pct < 0 ? "↓" : "→";
  return `${arrow} ${abs.toFixed(2)}%`;
};

export default function CorrelationTab() {
  const { t, lang } = useI18n();
  const { current } = useEngagement();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [running, setRunning] = useState(false);
  const [recategorising, setRecategorising] = useState(false);
  const [drilldown, setDrilldown] = useState(null);

  // Pair-picker state (V10). Catalog is app-wide, selectedPairs is
  // per-engagement; on load we hydrate from doc.selected_pairs if the
  // cache carries them, else fall back to the catalog's default preset.
  const [catalog, setCatalog] = useState(null);
  const [selectedPairs, setSelectedPairs] = useState([]);
  const [pickA, setPickA] = useState("");
  const [pickB, setPickB] = useState("");

  // Variance renderers close over the current t() so they translate live.
  const renderVarianceSide = (side) => {
    if (!side) return null;
    const label = (side.label || "").replace(/_/g, " ");
    if (side.insufficient_baseline) return t("correlationTab.earlyPeriodZero").replace("{label}", label);
    if (side.delta_pct == null)     return t("correlationTab.dashPlaceholder").replace("{label}", label);
    return `${label} ${fmtVariancePct(side.delta_pct)}`;
  };
  const renderVarianceCell = (v) => {
    if (!v || !v.series_a || v.series_a.window == null) return "—";
    const a = renderVarianceSide(v.series_a);
    const b = renderVarianceSide(v.series_b);
    return `${a} — ${b}`;
  };

  const load = async () => {
    if (!current) return;
    setLoading(true); setErr("");
    try {
      const r = await api.get(`/digital-audit/${current.id}/correlation`, { _silent: true });   // Batch 33 · T1: 404 = not run yet
      setDoc(r.data || null);
    } catch (e) {
      if (e?.response?.status === 404) setDoc(null);
      else setErr(toErrMsg(e, t("correlationTab.toastLoadFail")));
    } finally { setLoading(false); }
  };

  const loadCatalog = async () => {
    try {
      const r = await api.get(`/digital-audit/categories`);
      setCatalog(r.data);
    } catch (e) {
      // Non-fatal — falls back to the read-only view without the picker.
      setCatalog(null);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [current?.id]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadCatalog(); }, []);

  // Hydrate the pair picker: prefer doc.selected_pairs (per-engagement
  // memory), else catalog.default_pairs (app-wide preset).
  useEffect(() => {
    if (!catalog) return;
    const stored = (doc?.selected_pairs && doc.selected_pairs.length)
      ? doc.selected_pairs
      : catalog.default_pairs || [];
    setSelectedPairs(stored.map(([a, b]) => [a, b]));
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [catalog, doc?.computed_at]);

  const pollJob = async (jobId) => {
    let elapsed = 0;
    const MAX = 15 * 60 * 1000;
    while (elapsed < MAX) {
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      elapsed += POLL_INTERVAL_MS;
      const jr = await api.get(`/jobs/${jobId}`);
      const s = jr.data?.status;
      if (s === "done") return jr.data;
      if (s === "failed") throw new Error(jobErrorText(jr.data, lang, t, t("correlationTab.jobFailed")));
    }
    throw new Error(t("correlationTab.jobTimedOut"));
  };

  const recompute = async () => {
    if (!current) return;
    setRunning(true);
    try {
      // Send selected_pairs only if user has customised. Otherwise
      // omit so the worker uses DEFAULT_PAIRS — keeps the request
      // idempotent with the preset even if the catalog changes later.
      const body = selectedPairs.length ? { selected_pairs: selectedPairs } : {};
      const r = await api.post(
        `/digital-audit/${current.id}/correlation/recompute`, body);
      const jobId = r.data?.job_id;
      if (!jobId) throw new Error(t("correlationTab.enqueueFailed"));
      toast.info(t("correlationTab.toastRecomputeQueued"));
      await pollJob(jobId);
      toast.success(t("correlationTab.toastRecomputeComplete"));
      await load();
    } catch (e) {
      toast.error(toErrMsg(e, t("correlationTab.toastRecomputeFailed")));
    } finally { setRunning(false); }
  };

  const recategorise = async () => {
    if (!current || !doc) return;
    setRecategorising(true);
    try {
      await api.post(`/digital-audit/${current.id}/recategorise`);
      toast.success(t("correlationTab.toastCategoriesRefreshed"));
      await load();
    } catch (e) {
      toast.error(toErrMsg(e, t("correlationTab.toastRecategoriseFail")));
    } finally { setRecategorising(false); }
  };

  const exportAnalysis = async (fmt) => {
    if (!current || !doc) return;
    const token = localStorage.getItem("af_token");
    try {
      const resp = await fetch(
        `${API_URL}/digital-audit/${current.id}/correlation/export?fmt=${fmt}`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) {
        let msg = t("correlationTab.toastExportFailedStatus").replace("{status}", resp.status);
        try {
          const j = await resp.json();
          if (j?.detail) msg = j.detail;
        } catch { /* non-JSON error body */ }
        toast.error(msg);
        return;
      }
      const blob = await resp.blob();
      const cd = resp.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const fname = (m && m[1]) || `correlation.${fmt}`;
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = fname;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(t("correlationTab.toastExportedFmt").replace("{fmt}", fmt.toUpperCase()));
    } catch (e) {
      toast.error(toErrMsg(e, t("correlationTab.toastExportFailed")));
    }
  };

  // ---------- Pair picker helpers ----------
  const selectedKeys = useMemo(
    () => new Set(selectedPairs.map(([a, b]) => pairKey(a, b))),
    [selectedPairs]);

  // Detect "picker changed but not yet recomputed" so the disconnect
  // between chip selection and the results table isn't silent.
  const cachedKeys = useMemo(() => new Set(
    asArray(doc?.pairs).map((p) => pairKey(p.series_a, p.series_b))
  ), [doc?.pairs]);
  const selectionDiffersFromCache = useMemo(() => {
    if (!doc) return false;
    if (selectedKeys.size !== cachedKeys.size) return true;
    for (const k of selectedKeys) if (!cachedKeys.has(k)) return true;
    return false;
  }, [selectedKeys, cachedKeys, doc]);

  // Resizable column widths (persisted per-table via localStorage).
  const pairsCols = useResizableColumns({
    storageKey: "digital-audit/correlation-pairs",
    defaults: [260, 90, 110, 100, 100, 220, 220, 380],
  });
  const monthlyCols = useResizableColumns({
    storageKey: "digital-audit/correlation-monthly",
    defaults: [110, 220, 160, 100, 120],
  });

  const addPair = () => {
    if (!catalog) return;
    if (!pickA || !pickB) {
      toast.error(t("correlationTab.toastPickTwo"));
      return;
    }
    if (pickA === pickB) {
      toast.error(t("correlationTab.toastPairSidesDiffer"));
      return;
    }
    const k = pairKey(pickA, pickB);
    if (selectedKeys.has(k)) {
      toast.error(t("correlationTab.toastPairAlreadySelected"));
      return;
    }
    if (selectedPairs.length >= (catalog.max_pairs || 20)) {
      toast.error(t("correlationTab.toastCapPairs").replace("{n}", catalog.max_pairs || 20));
      return;
    }
    setSelectedPairs([...selectedPairs, [pickA, pickB]]);
    setPickA(""); setPickB("");
  };

  const removePair = (idx) => {
    setSelectedPairs(selectedPairs.filter((_, i) => i !== idx));
  };

  const resetToDefaults = () => {
    if (!catalog) return;
    setSelectedPairs(asArray(catalog.default_pairs).map(([a, b]) => [a, b]));
    toast.info(t("correlationTab.toastPairsReset"));
  };

  if (!current) return null;

  const cats = catalog?.categories || [];
  const interpColumnHeader = catalog?.column?.header || t("correlationTab.interpretationDefault");
  const interpTooltip = catalog?.column?.tooltip
    || t("correlationTab.interpretationTooltipDefault");

  return (
    <TooltipProvider>
    <div className="space-y-4 min-w-0 w-full" data-testid="correlation-tab">
      <TbOnlyNotice status={doc} testid="correlation-tb-only" />
      <Card className="min-w-0">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">{t("correlationTab.title")}</CardTitle>
            <div className="flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline" disabled={!doc}
                           data-testid="correlation-download">
                    <Download className="h-3 w-3 me-1" />
                    {t("correlationTab.downloadBtn")}
                    <ChevronDown className="h-3 w-3 ms-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => exportAnalysis("csv")}
                                     data-testid="correlation-download-csv">
                    {t("correlationTab.downloadCsv")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => exportAnalysis("xls")}
                                     data-testid="correlation-download-xls">
                    {t("correlationTab.downloadXls")}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => exportAnalysis("xlsx")}
                                     data-testid="correlation-download-xlsx">
                    {t("correlationTab.downloadXlsx")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button size="sm" variant="outline"
                       onClick={recategorise}
                       disabled={!doc || recategorising}
                       data-testid="correlation-recategorise">
                {recategorising
                  ? <Loader2 className="h-3 w-3 me-1 animate-spin" />
                  : <Wand2 className="h-3 w-3 me-1" />}
                {t("correlationTab.recategoriseBtn")}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="min-w-0 overflow-x-auto">
          {catalog && (
            <div className="mb-4 rounded-md border bg-muted/40 p-3 min-w-0"
                 data-testid="correlation-pair-picker">
              <div className="mb-2 flex items-center justify-between">
                <div className="text-sm font-medium">
                  {t("correlationTab.pairSelection")}
                  <span className="ms-2 text-xs text-muted-foreground">
                    ({selectedPairs.length}/{catalog.max_pairs || 20})
                  </span>
                </div>
                <Button size="sm" variant="ghost" onClick={resetToDefaults}
                         data-testid="correlation-reset-pairs">
                  <RotateCcw className="h-3 w-3 me-1" />
                  {t("correlationTab.resetDefaults")}
                </Button>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Select value={pickA} onValueChange={setPickA}>
                  <SelectTrigger className="w-56" data-testid="pair-pick-a">
                    <SelectValue placeholder={t("correlationTab.categoryAPh")} />
                  </SelectTrigger>
                  <SelectContent className="max-h-64">
                    {cats.map((c) => (
                      <SelectItem key={c} value={c}
                                   data-testid={`pair-pick-a-opt-${c}`}>
                        {c.replace(/_/g, " ")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-sm text-muted-foreground">↔</span>
                <Select value={pickB} onValueChange={setPickB}>
                  <SelectTrigger className="w-56" data-testid="pair-pick-b">
                    <SelectValue placeholder={t("correlationTab.categoryBPh")} />
                  </SelectTrigger>
                  <SelectContent className="max-h-64">
                    {cats.map((c) => (
                      <SelectItem key={c} value={c}
                                   data-testid={`pair-pick-b-opt-${c}`}>
                        {c.replace(/_/g, " ")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button size="sm" variant="outline" onClick={addPair}
                         data-testid="pair-add">
                  <Plus className="h-3 w-3 me-1" />
                  {t("correlationTab.addPair")}
                </Button>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {selectedPairs.map(([a, b], i) => (
                  <Badge key={i} variant="secondary"
                          className="gap-1"
                          data-testid={`selected-pair-${a}-${b}`}>
                    {a.replace(/_/g, " ")} ↔ {b.replace(/_/g, " ")}
                    <button
                      onClick={() => removePair(i)}
                      className="ms-1 rounded-full opacity-70 hover:opacity-100"
                      data-testid={`remove-pair-${a}-${b}`}
                      aria-label={t("correlationTab.removeAria").replace("{a}", a).replace("{b}", b)}>
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
                {selectedPairs.length === 0 && (
                  <span className="text-xs text-muted-foreground">
                    {t("correlationTab.noPairsSelected")}
                  </span>
                )}
              </div>
              {selectionDiffersFromCache && (
                <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900"
                     data-testid="pair-selection-dirty-hint">
                  {t("correlationTab.selectionDirtyPre")}<span className="font-medium">{t("correlationTab.selectionDirtyBold")}</span>{t("correlationTab.selectionDirtyPost")}
                </div>
              )}
            </div>
          )}
          {err && (
            <div className="mb-3 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900"
                 data-testid="correlation-error">{err}</div>
          )}
          {!loading && (   /* Batch 27 · Add-on 2 Step 2: the shared run-state banner (Run / Rescan) */
            <div className="mb-3">
              <StoredResultBanner lang={lang} state={doc ? (doc.run_state?.state || "fresh") : "not_run"}
                                  runAt={doc?.run_state?.run_at || doc?.computed_at} runBy={doc?.run_state?.run_by_name}
                                  running={running} onRun={recompute} runTestId="correlation-recompute"
                                  testid={doc ? "correlation-run-state" : "correlation-empty"} />
            </div>
          )}
          {doc && (
            <div className="space-y-4" data-testid="correlation-loaded">
              <div className="text-xs text-muted-foreground">
                {t("correlationTab.computedLine")
                  .replace("{when}", fmtDateTimeFirm(doc.computed_at))
                  .replace("{rows}", doc.gl_row_count?.toLocaleString?.() ?? doc.gl_row_count)
                  .replace("{unmapped}", doc.unmapped_account_count ?? 0)}
              </div>

              <FyPeriodNote period={doc.fy_period} testid="correlation-fy"
                            blocks={[{ key: "monthly", title: t("s7b.period.pyMonthly"),
                                       rows: asArray(doc.prior_year_monthly_aggregates).map((r) => ({
                                         key: `${r.period}-${r.category}`, month: `${fmtMonth(r.period)} · ${r.category}`,
                                         value: fmtNumber(r.net_movement) })) }]} />
              <ReadoutCard engagementId={current?.id} page="correlation" analysisComputedAt={doc.computed_at} />

              <div className="overflow-x-auto min-w-0">
                <table className="text-sm"
                       style={{ tableLayout: "fixed",
                                width: pairsCols.colWidths.reduce((a,b)=>a+b,0) }}>
                  <ResetCaption cols={pairsCols} />
                  <colgroup>
                    {pairsCols.colWidths.map((w, i) => (
                      <col key={i} style={{ width: w }} />
                    ))}
                  </colgroup>
                  <thead className="text-start text-xs uppercase text-muted-foreground">
                    <tr>
                      <ResizableTh index={0} onResizeStart={pairsCols.onResizeStart}
                                    className="py-2 pe-4">{t("correlationTab.colPair")}</ResizableTh>
                      <ResizableTh index={1} onResizeStart={pairsCols.onResizeStart}
                                    className="py-2 pe-4">{t("correlationTab.colNPeriods")}</ResizableTh>
                      <ResizableTh index={2} onResizeStart={pairsCols.onResizeStart}
                                    className="py-2 pe-4">{t("correlationTab.colKendall")}</ResizableTh>
                      <ResizableTh index={3} onResizeStart={pairsCols.onResizeStart}
                                    className="py-2 pe-4">{t("correlationTab.colPValue")}</ResizableTh>
                      <ResizableTh index={4} onResizeStart={pairsCols.onResizeStart}
                                    className="py-2 pe-4">{t("correlationTab.colPearson")}</ResizableTh>
                      <ResizableTh index={5} onResizeStart={pairsCols.onResizeStart}
                                    className="py-2 pe-4">{t("correlationTab.colReading")}</ResizableTh>
                      <ResizableTh index={6} onResizeStart={pairsCols.onResizeStart}
                                    className="py-2 pe-4">
                        <span className="inline-flex items-center gap-1">
                          {t("correlationTab.variance")}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button type="button"
                                      className="opacity-70 hover:opacity-100"
                                      data-testid="variance-tooltip-trigger"
                                      aria-label={t("correlationTab.whatVarianceAria")}>
                                <Info className="h-3 w-3" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-sm">
                              <p className="text-xs leading-relaxed"
                                 data-testid="variance-tooltip-body">
                                {t("correlationTab.varianceTooltip")}
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </span>
                      </ResizableTh>
                      <ResizableTh index={7} onResizeStart={pairsCols.onResizeStart}
                                    className="py-2 pe-4">
                        <span className="inline-flex items-center gap-1">
                          {interpColumnHeader}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button type="button"
                                      className="opacity-70 hover:opacity-100"
                                      data-testid="interpretation-tooltip-trigger"
                                      aria-label={t("correlationTab.whatInterpretationAria")}>
                                <Info className="h-3 w-3" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-sm">
                              <p className="text-xs leading-relaxed"
                                 data-testid="interpretation-tooltip-body">
                                {interpTooltip}
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </span>
                      </ResizableTh>
                    </tr>
                  </thead>
                  <tbody>
                    {asArray(doc.pairs).map((p, i) => (
                      <tr key={i} className="border-t align-top"
                          data-testid={`correlation-pair-${p.series_a}-${p.series_b}`}>
                        <td className="py-2 pe-4">
                          <span className="font-medium">{p.series_a.replace(/_/g, " ")}</span>
                          {" ↔ "}
                          <span className="font-medium">{p.series_b.replace(/_/g, " ")}</span>
                        </td>
                        <td className="py-2 pe-4">{p.n_periods}</td>
                        <td className="py-2 pe-4 font-mono">
                          {p.kendall_tau?.toFixed?.(4) ?? "—"}
                        </td>
                        <td className="py-2 pe-4 font-mono">
                          {p.kendall_p_value?.toFixed?.(4) ?? "—"}
                        </td>
                        <td className="py-2 pe-4 font-mono">
                          {p.pearson_r?.toFixed?.(4) ?? "—"}
                        </td>
                        <td className="py-2 pe-4 text-xs">{p.reading}</td>
                        <td className="py-2 pe-4 text-xs whitespace-normal break-words"
                            data-testid={`variance-${p.series_a}-${p.series_b}`}>
                          {renderVarianceCell(p.variance)}
                        </td>
                        <td className="py-2 pe-4 text-xs leading-relaxed whitespace-normal break-words overflow-hidden"
                            data-testid={`interpretation-${p.series_a}-${p.series_b}`}>
                          {p.interpretation || ""}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div>
                <div className="mb-2 text-sm font-medium">{t("correlationTab.monthlyAggregates")}</div>
                <div className="overflow-x-auto min-w-0">
                  <table className="text-sm"
                         style={{ tableLayout: "fixed",
                                  width: monthlyCols.colWidths.reduce((a,b)=>a+b,0) }}>
                    <ResetCaption cols={monthlyCols} />
                    <colgroup>
                      {monthlyCols.colWidths.map((w, i) => (
                        <col key={i} style={{ width: w }} />
                      ))}
                    </colgroup>
                    <thead className="text-start text-xs uppercase text-muted-foreground">
                      <tr>
                        <ResizableTh index={0} onResizeStart={monthlyCols.onResizeStart}
                                      className="py-2 pe-4">{t("correlationTab.colPeriod")}</ResizableTh>
                        <ResizableTh index={1} onResizeStart={monthlyCols.onResizeStart}
                                      className="py-2 pe-4">{t("correlationTab.colCategory")}</ResizableTh>
                        <ResizableTh index={2} onResizeStart={monthlyCols.onResizeStart}
                                      className="py-2 pe-4 text-end">{t("correlationTab.colNetMovement")}</ResizableTh>
                        <ResizableTh index={3} onResizeStart={monthlyCols.onResizeStart}
                                      className="py-2 pe-4 text-end">{t("correlationTab.colEntries")}</ResizableTh>
                        <ResizableTh index={4} onResizeStart={monthlyCols.onResizeStart}
                                      className="py-2 pe-4"></ResizableTh>
                      </tr>
                    </thead>
                    <tbody>
                      {asArray(doc.monthly_aggregates).map((r, i) => (
                        <tr key={i} className="border-t"
                            data-testid={`correlation-monthly-${r.period}-${r.category}`}>
                          <td className="py-2 pe-4">{fmtMonth(r.period)}</td>
                          <td className="py-2 pe-4">{r.category.replace(/_/g, " ")}</td>
                          <td className="py-2 pe-4 text-end font-mono">
                            {fmtNumber(r.net_movement)}
                          </td>
                          <td className="py-2 pe-4 text-end font-mono">
                            {r.entry_count}
                          </td>
                          <td className="py-2 pe-4 text-end">
                            <Button size="sm" variant="ghost"
                                     onClick={() => setDrilldown({
                                       period: r.period,
                                       category: r.category,
                                     })}
                                     data-testid={`drilldown-${r.period}-${r.category}`}>
                              <TableProperties className="h-3 w-3 me-1" />
                              {t("correlationTab.drillIn")}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {drilldown && (
        <DrilldownModal
          engagementId={current.id}
          track="correlation"
          period={drilldown.period}
          category={drilldown.category}
          onClose={() => setDrilldown(null)}
        />
      )}
    </div>
    </TooltipProvider>
  );
}
